import wordsCount from 'words-count'

import { type IFetchSSEOptions, fetchSSE } from '../core'

import type { ISseResult, TSseBaseEvent } from './types'

export interface TCustomEvent {
  type: string
  data: Record<string, any>
}

export interface IGeneratedBaseResult extends ISseResult {
  type: 'OK' | 'Client Abort'
}

export interface IGeneratedServerAbortResult extends ISseResult {
  type: 'Server Abort'
  error: {
    code: number
    message: string
  }
}

export interface IGeneratedInfo<P = Record<string, any> & { _type?: string }> {
  resource: string
  payload: P
}

export type TGeneratedResult =
  | IGeneratedBaseResult
  | IGeneratedServerAbortResult

export const GeneratingErrorEventNames = ['EXCEPTION-CHAT', 'EXCEPTION_CHAT']

export interface IAbortableFetchSSEOptions
  extends Omit<IFetchSSEOptions, 'body' | 'onMessage' | 'onEvent'> {
  payload: Record<string, any>
  onEvent: (event: TSseBaseEvent) => void
  /**
   * 由于自定义事件类型 TCustomEvent 不能在 onEvent 结合形成良好的类型推导，因此使用额外的回调处理，
   * ref: https://github.com/microsoft/TypeScript/issues/47178
   *
   * 生成中异常 event 通过 onGeneratingError 处理
   */
  onCustomEvent?: (customEvent: TCustomEvent) => void
  onGeneratingError?: (code: number, message: string) => void
  /**
   * SSE 生成结束后的回调，包括以下情况：
   *
   * - [ok] SSE 正常结束
   * - [client-abort] 用户主动中断结束
   * - [server-abort] 生成中异常结束
   *
   * 当前设计使用方式：
   *
   * - SSE 接口数据上报
   */
  onGenerated?: (info: IGeneratedInfo, result: TGeneratedResult) => void
}

/**
 * 统一项目内 SSE 请求的基础业务封装
 *
 * - 'post' 方法实现，支持 payload
 * - 处理中断异常，包括客户端中断和服务端中断
 * - onEvent 拆分 answer/done
 * - onCustomEvent 处理其他自定义事件类型
 * - onGenerated 支持 SSE 结束后的请求详情回调
 */
export async function abortableFetchSSE(
  resource: string,
  options: IAbortableFetchSSEOptions,
) {
  let generated = ''
  let generatingError = false

  const {
    payload,
    signal,
    onEvent,
    onCustomEvent,
    onGenerated,
    onGeneratingError,
    ...fetchSseOptions
  } = options
  const apiInfo = { resource, payload }

  try {
    await fetchSSE(resource, {
      // 即使跨域也尝试带上 cookie
      credentials: 'include',
      method: 'POST',
      ...fetchSseOptions,
      signal,
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        ...fetchSseOptions.headers,
      },
      onEvent(parsedEvent) {
        if (parsedEvent.data === '[DONE]') {
          const result = {
            payload,
            content: generated,
            words: wordsCount(generated),
          }
          if (!generatingError && onGenerated) {
            onGenerated(apiInfo, {
              type: 'OK',
              ...result,
            })
          }
          onEvent({
            type: 'done',
            serverAbort: generatingError,
            result,
          })
          return
        }

        let data: Record<string, any>
        try {
          data = JSON.parse(parsedEvent.data)
        } catch (err) {
          console.error(err)
          return
        }
        // 常规 SSE 数据返回 event 为空，
        // 特殊的自定义事件返回时特殊处理
        if (parsedEvent.event) {
          if (
            GeneratingErrorEventNames.includes(parsedEvent.event?.toUpperCase())
          ) {
            generatingError = true
            onGeneratingError?.(data.errorCode, data.errorMessage)

            if (onGenerated) {
              onGenerated(apiInfo, {
                type: 'Server Abort',
                content: generated,
                words: wordsCount(generated),
                error: {
                  code: data.errorCode,
                  message: data.errorMessage,
                },
              })
            }
          } else {
            onCustomEvent?.({
              type: parsedEvent.event,
              data,
            })
          }
        } else {
          const { content } = data
          if (content) {
            generated += content
            onEvent({
              type: 'answer',
              content,
            })
          }
        }
      },
    })
  } catch (err) {
    // 中断异常不需要 onEvent 处理
    if (err instanceof DOMException && err.name === 'AbortError') {
      // eslint-disable-next-line no-console
      console.debug((err as any).message)
      if (onGenerated) {
        onGenerated(apiInfo, {
          type: 'Client Abort',
          content: generated,
          words: wordsCount(generated),
        })
      }
      return
    }
    throw err
  }
}
